import type React from 'react';
import { Search, ShoppingBag } from 'lucide-react';
import type { StorefrontProduct } from '../../api/client';
import { useElementBox, type ElementComponent } from './elementRenderContext';
import { useProduct, useProductVariant } from './productContext';
import { buttonLabel, buyButton, pricedLabel, usePurchase, VariantSelect, type Purchase } from './usePurchase';
import { CheckoutContent } from '../../commerce/ShopCommerce';
import { OrderConfirmation } from '../../commerce/OrderConfirmation';
import type { PageLine } from '../../commerce/usePageLineCart';
import { sampleProducts } from './sampleCatalog';
import { boolProp, choiceProp, textProp } from './elementProps';
import { OptionPills, QuantityStepper } from './PurchaseControls';
import { productOptions, valueInStock, variantFor, variantValues } from './purchaseChoice';
import { PopupCheckoutButton, usePopupCheckout } from './PopupCheckout';
import { maxQuantity } from '../../commerce/variantStock';
import { inStoreLanguage, shopWords } from '../../commerce/shopWords';
import { orderWords } from '../../commerce/orderWords';
import { OfferLadder } from './OfferLadder';
import { named, shopNames } from '../../commerce/shopNames';

// Buying the product in scope: its choices, the merchant's quantity offer, a quantity, the button that puts it in
// the order, and — as on Sleek's product page — an optional "Buy it now" that goes straight to checkout. Variant ids, never labels
// or prices the page computed, are what the server orders.

/** How a product's choices are drawn: one dropdown, or a row of pills per choice. */
const OPTION_STYLES = ['dropdown', 'buttons'] as const;

// Draws each choice as a row of pills, and picks the variant the picks name.
const PillChoices: React.FC<{ buy: Purchase }> = ({ buy }) => {
  const { product, variant } = buy;
  if (!product || !variant) return null;
  const picks = variantValues(product, variant);
  // Picks the variant named by the choices with this one changed.
  const pick = (index: number, value: string) => {
    const next = variantFor(product, picks.map((current, at) => (at === index ? value : current)));
    if (next) buy.select(next.id);
  };
  return <>{productOptions(product).map((option, index) => (
    <OptionPills key={option.name} option={option} picked={picks[index] ?? ''} disabled={false}
      inStock={(value) => buy.sample || valueInStock(product, picks, index, value)} onPick={(value) => pick(index, value)} />
  ))}</>;
};

// Draws the quantity as the plain field the theme has always offered.
const QuantityField: React.FC<{ buy: Purchase }> = ({ buy }) => (
  <label className="xe-purchase-field"><span>{shopWords(buy.commerce?.language).quantity}</span><input type="number" min={1} max={maxQuantity(buy.variant)} value={buy.quantity}
    onChange={(event) => buy.setQuantity(Math.max(1, Math.min(99, Math.trunc(Number(event.target.value) || 1))))} /></label>
);

// Orders the chosen variant and quantity at once, as Shopify's Buy it now: the form over the page in a popup store, else
// its page, holding this product alone — the cart is never touched, so pressing it again never adds to anything.
function buyNow(buy: Purchase, popup: boolean): void {
  if (buy.locked || !buy.product || !buy.variant || !buy.commerce) return;
  buy.commerce.buyNow({ product: buy.product, variantId: buy.variant.id, quantity: buy.quantity }, !popup);
}

// Draws only the choices and the quantity, for a page whose own order form takes the order; with a cart already open there is nothing to choose.
const PageChoices: React.FC<{ buy: Purchase; pills: boolean }> = ({ buy, pills }) => {
  if (buy.commerce?.lines.length && !buy.locked) return null;
  const multiple = (buy.product?.variants.length ?? 0) > 1;
  return <div className="xe-purchase">
    {multiple && (pills ? <PillChoices buy={buy} /> : <VariantSelect buy={buy} disabled={false} />)}
    <OfferLadder buy={buy} />
    <div className="xe-purchase-qty"><span>{shopWords(buy.commerce?.language).quantity}</span>
      <QuantityStepper value={buy.quantity} max={maxQuantity(buy.variant)} disabled={!buy.available && !buy.locked} onChange={buy.setQuantity} /></div>
  </div>;
};

// Sends the shopper to a product's own page, where its options are chosen.
const openProduct = (product: StorefrontProduct | null): void => {
  if (product && !product.id.startsWith('sample-')) window.location.assign(`/products/${encodeURIComponent(product.slug)}`);
};

// Opens the quick view on a product where the page has one, else its own page.
const lookAt = (buy: Purchase): void => (buy.commerce && buy.product ? buy.commerce.peek(buy.product) : openProduct(buy.product));

/** What a card's buttons are drawn with: the box its element is styled by, its label, whether a quick view is offered, and whether the button is a bag. */
type QuickAddProps = { buy: Purchase; box: ReturnType<typeof useElementBox>; label: string; quickView: boolean; bag: boolean; looks: boolean };

// Draws a card's buttons: the pill adds a product with one option and opens one with several, and a circle opens its quick view.
const QuickAdd: React.FC<QuickAddProps> = ({ buy, box, label, quickView, bag, looks }) => {
  const multiple = (buy.product?.variants.length ?? 0) > 1;
  const words = looks ? label : buttonLabel(buy, { label }, multiple);
  // Adds a one-option product at once, and opens one with choices to make.
  const add = () => {
    if (multiple || looks) return lookAt(buy);
    if (!buy.locked && buy.product && buy.variant) buy.commerce?.add(buy.product, buy.variant.id, 1);
  };
  // The words and the bag are both drawn and the stylesheet shows one, since a phone shows the bag whatever was chosen;
  // the name a screen reader hears says which product, as a row of cards is otherwise a row of identical buttons.
  return <>
    <button
      {...box} type="button" {...(multiple || looks ? { disabled: false } : buyButton(buy.locked, buy.available))} onClick={add}
      aria-label={buy.product ? `${words}: ${buy.product.title}` : words} data-look={bag ? 'bag' : undefined}
    >
      <span className="xe-atc-words">{words}</span>
      <ShoppingBag size={18} strokeWidth={1.5} aria-hidden="true" className="xe-atc-bag" />
    </button>
    {quickView && buy.product ? (
      <button type="button" className="xe-pcard-quick" aria-label={named(shopNames(buy.commerce?.language).quickView, { title: buy.product.title })} onClick={() => lookAt(buy)}>
        <Search size={20} strokeWidth={1.5} aria-hidden="true" />
      </button>
    ) : null}
  </>;
};

// Draws the purchase controls for the product in scope, in the style the merchant chose.
export const PurchaseElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const { className: boxClassName, ...boxProps } = box;
  const buy = usePurchase();
  const popup = usePopupCheckout();
  const words = shopWords(buy.commerce?.language);
  if (boolProp(node.props, 'quickAdd')) return <QuickAdd buy={buy} box={box} label={inStoreLanguage(textProp(node.props, 'label', ''), shopWords('en').addToCart, words.addToCart)} quickView={boolProp(node.props, 'quickView')} looks={boolProp(node.props, 'onlyLooks')} bag={boolProp(node.props, 'iconOnly')} />;
  // A bundle row names its option inline, as Sleek's "50ml ⌄"; a product with one option has nothing to choose.
  if (boolProp(node.props, 'bundleOnly')) return (buy.product?.variants.length ?? 0) > 1
    ? <div {...boxProps} className={['xe-purchase xe-purchase--inline', boxClassName].filter(Boolean).join(' ')}><VariantSelect buy={buy} disabled={buy.locked} /></div>
    : null;
  const pills = choiceProp(node.props, 'optionStyle', OPTION_STYLES, 'dropdown') === 'buttons';
  if (boolProp(node.props, 'orderHere')) return <PageChoices buy={buy} pills={pills} />;
  const multiple = (buy.product?.variants.length ?? 0) > 1;
  const showing = pills || boolProp(node.props, 'showOptions');
  const stepper = boolProp(node.props, 'stepper');
  // Puts the chosen variant and quantity in the order, or opens a product whose choices this box does not show.
  const add = () => {
    if (buy.locked) return;
    // A product whose options this box does not show is chosen on its own page, never in a form unfolding here.
    if (multiple && !showing) return openProduct(buy.product);
    if (buy.product && buy.variant) buy.commerce?.add(buy.product, buy.variant.id, buy.quantity);
  };
  return <div className="xe-purchase">
    {showing && multiple && (pills ? <PillChoices buy={buy} /> : <VariantSelect buy={buy} disabled={false} />)}
    {showing && <OfferLadder buy={buy} />}
    {showing && !stepper && !boolProp(node.props, 'hideQuantity') && <QuantityField buy={buy} />}
    <div className="xe-purchase-row">
      {(showing || pills) && stepper && <QuantityStepper value={buy.quantity} max={maxQuantity(buy.variant)} disabled={!buy.available && !buy.locked} onChange={buy.setQuantity} />}
      <button {...box} type="button" {...buyButton(buy.locked, buy.available)} onClick={add}>{pricedLabel(buy, node.props, multiple && !showing)}</button>
    </div>
    {boolProp(node.props, 'buyNow') && <button type="button" className="xe-purchase-buy-now" {...buyButton(buy.locked, buy.available)} onClick={() => buyNow(buy, popup)}>{inStoreLanguage(textProp(node.props, 'buyNowLabel', ''), shopWords('en').buyItNow, words.buyItNow)}</button>}
  </div>;
};

// Reads the product block's own choice, which a COD page's form orders when the cart is empty.
function usePageLine(enabled: boolean): PageLine | null {
  const product = useProduct();
  const { selected, quantity, scoped } = useProductVariant();
  if (!enabled || !scoped || !product || product.status !== 'ACTIVE' || product.id.startsWith('sample-')) return null;
  const variantId = selected || product.variants[0]?.id;
  return variantId ? { product, variantId, quantity } : null;
}

// Checkout and confirmation stay placeable and stylable as part of the theme's page tree.
export const CheckoutElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const pageLine = usePageLine(boolProp(node.props, 'orderPageProduct'));
  const popup = usePopupCheckout();
  return <div {...box}>
    {popup && pageLine
      ? <PopupCheckoutButton line={pageLine} />
      : <CheckoutContent samples={sampleProducts(node.props, 2)} showHeading={boolProp(node.props, 'showHeading', true)} pageLine={pageLine} />}
  </div>;
};
// Answers the confirmation heading a merchant wrote, or nothing while it is the theme's English default, so the store's language words it.
const headingInStoreLanguage = (written: string): string | undefined =>
  (written.trim() === '' || written.trim() === orderWords('en').thankYou ? undefined : written);

// Draws the order confirmation where the theme placed it, with its heading and steps as the merchant set them.
export const OrderSummaryElement: ElementComponent = ({ node }) => <div {...useElementBox(node)}>
  <OrderConfirmation heading={headingInStoreLanguage(textProp(node.props, 'heading', ''))} showSteps={boolProp(node.props, 'showSteps', true)} />
</div>;
